import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, EnvelopeSimple, ArrowRight, Spinner, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

// Owner-only allowlist — enforced on the client for a fast, clear error message.
// The backend also enforces this via bcrypt-seeded users so this is a UX guard, not a security boundary.
const OWNER_EMAILS = [
  "david.josephindustries@gmail.com",
  "josephindustriesllc@gmail.com",
];

export default function AdminLoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && typeof user === "object") {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    const clean = email.trim().toLowerCase();
    if (!OWNER_EMAILS.includes(clean)) {
      const msg = "This portal is restricted to Joseph Industries owner accounts.";
      setErr(msg);
      toast.error(msg);
      return;
    }

    setSubmitting(true);
    try {
      await login(clean, password);
      toast.success("Welcome back — Owner Command Center loading.");
      const to = location.state?.from?.pathname || "/admin";
      navigate(to, { replace: true });
    } catch (e2) {
      const msg = formatApiErrorDetail(e2?.response?.data?.detail) || e2.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="ji-container py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="ji-overline mb-5 inline-flex items-center gap-2">
            <ShieldCheck size={14} weight="bold" /> Repair Dispatch LA · Owner Access
          </div>
          <h1
            style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }}
            className="text-5xl md:text-6xl font-bold tracking-tight"
          >
            Owner<br />
            <span className="text-[var(--ji-accent)] italic">Command Center.</span>
          </h1>
          <p className="mt-6 text-lg text-[var(--ji-muted)] leading-relaxed max-w-md">
            Sign in to review revenue, work leads, monitor site traffic, and moderate customer reviews — all from one dashboard.
          </p>

          <div className="mt-10 space-y-3 max-w-md">
            {[
              "Revenue by day, week, month & all-time",
              "Leads pipeline & one-click status updates",
              "Live site traffic & conversion tracking",
              "Review moderation hub",
            ].map((t) => (
              <div key={t} className="flex items-center gap-3 text-sm text-[var(--ji-text)]">
                <span className="inline-block h-1.5 w-1.5 bg-[var(--ji-accent)]" style={{ borderRadius: 999 }} />
                {t}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6">
          <form
            onSubmit={onSubmit}
            data-testid="admin-login-form"
            className="ji-card p-7 md:p-10"
            style={{ borderRadius: 4 }}
          >
            <div className="inline-flex items-center gap-2 mb-2 text-xs uppercase tracking-[0.2em] text-[var(--ji-accent)] font-semibold">
              <Lock size={12} weight="bold" /> Restricted Access
            </div>
            <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold tracking-tight">
              Sign in
            </h2>
            <p className="text-sm text-[var(--ji-muted)] mt-1">
              This portal is limited to Joseph Industries owner accounts.
            </p>

            <div className="mt-7 space-y-5">
              <div>
                <Label htmlFor="admin-email" className="text-sm font-medium">Owner Email</Label>
                <div className="relative mt-2">
                  <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
                  <Input
                    id="admin-email"
                    type="email"
                    autoComplete="email"
                    data-testid="admin-login-email"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    className="h-12 pl-9"
                    placeholder="owner@josephindustries.com"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="admin-password" className="text-sm font-medium">Password</Label>
                <div className="relative mt-2">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
                  <Input
                    id="admin-password"
                    type="password"
                    autoComplete="current-password"
                    data-testid="admin-login-password"
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                    className="h-12 pl-9"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              {err && (
                <p data-testid="admin-login-error" className="text-sm text-red-600" role="alert">
                  {err}
                </p>
              )}

              <button
                type="submit"
                data-testid="admin-login-submit"
                disabled={submitting}
                className="ji-btn-primary w-full inline-flex items-center justify-center gap-2 h-14 text-base font-semibold disabled:opacity-60"
                style={{ borderRadius: 4 }}
              >
                {submitting ? (
                  <><Spinner size={18} className="animate-spin" /> Signing in…</>
                ) : (
                  <>Enter Command Center <ArrowRight size={18} weight="bold" /></>
                )}
              </button>
            </div>

            <div className="mt-6 text-xs text-[var(--ji-muted)] text-center">
              <Link
                to="/"
                className="hover:text-[var(--ji-accent)]"
                data-testid="admin-login-back-home"
              >
                ← Back to public site
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
